import React, { useMemo, useEffect, useRef, useState } from "react"
import { Ui89Card } from "./Ui89Card"
import { Ui89BoxShadow } from "./Ui89BoxShadow"
import { Ui89Scene } from "./Ui89Scene"
import GridExpandTrick from "./private/GridExpandTrick"
import ScrollContainer from "./private/ScrollContainer"

import "./Ui89ModalDialog.css"
import { Ui89Palette, Ui89Theme } from "../theme"
import { DialogRootContext } from "../DialogRootContext"

export interface Ui89ModalDialogProps {
  open: boolean
  size?: "small" | "medium" | "big" | "full"
  theme?:
    | Ui89Theme
    | keyof typeof Ui89Theme
    | Ui89Palette
    | keyof typeof Ui89Palette
  children?: React.ReactNode
  topLeft?: React.ReactNode
  topCenter?: React.ReactNode
  topRight?: React.ReactNode
  bottomLeft?: React.ReactNode
  bottomCenter?: React.ReactNode
  bottomRight?: React.ReactNode
  onRequestClose?: () => void
}

export function Ui89ModalDialog({
  open,
  size,
  theme,
  children,
  topLeft,
  topCenter,
  topRight,
  bottomLeft,
  bottomCenter,
  bottomRight,
  onRequestClose,
}: Ui89ModalDialogProps) {
  const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const callbackRef = (el: HTMLDialogElement | null) => {
    ;(dialogRef as React.MutableRefObject<HTMLDialogElement | null>).current =
      el
    setDialogEl(el)
  }

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open) {
      if (!dialog.open) dialog.showModal()
    } else {
      if (dialog.open) dialog.close()
    }
  }, [open])

  const dialogBoxClass = useMemo(() => {
    return [
      "ui89-modal-dialog__box",
      `ui89-modal-dialog__box--size-${size ?? "responsive"}`,
    ].join(" ")
  }, [size])

  function onClickContent(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) {
      onRequestClose?.()
    }
  }

  function onKeyDownDialog(e: React.KeyboardEvent<HTMLDialogElement>) {
    if (e.key === "Escape") {
      e.preventDefault()
      onRequestClose?.()
    }
  }

  return (
    <dialog
      ref={callbackRef}
      className="ui89-modal-dialog"
      onKeyDown={onKeyDownDialog}
    >
      <DialogRootContext.Provider value={dialogEl}>
        <div className="ui89-modal-dialog__content" onClick={onClickContent}>
          <div className="ui89-modal-dialog__spacer"></div>

          <div className={dialogBoxClass}>
            <Ui89BoxShadow>
              <GridExpandTrick>
                <Ui89Card
                  topLeft={topLeft}
                  topCenter={topCenter}
                  topRight={topRight}
                  bottomLeft={bottomLeft}
                  bottomCenter={bottomCenter}
                  bottomRight={bottomRight}
                  theme={theme}
                >
                  <ScrollContainer>{children}</ScrollContainer>
                </Ui89Card>
              </GridExpandTrick>
            </Ui89BoxShadow>
          </div>
        </div>
      </DialogRootContext.Provider>
    </dialog>
  )
}
